import { useEffect, useMemo } from 'react';
import { getSortField, getSortValue, sortKey } from '../../../../../features/table/helpers';
import { formatDateTimeRangeFromString, tzOffset } from '../../../../../helpers/date-time';
import { useLang } from '../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../hooks/use-query-params';
import {
  CheckInExportFormatType,
  FetchCheckInCheckOutRequest,
} from '../../../../../store/api/check-in-check-out-api';
import { useCheckInCheckOutStore } from '../../../../../store/use-check-in-check-out-store';
import { useEmployeesStore } from '../../../../../store/use-employee-store';
import { useTagStaffStore } from '../../../../../store/use-tag-staff-store';
import { IdValue, SortType } from '../../../../../types/props';
import { useBranchesStore } from '../../../../../store/use-branch-store';
import { defaultViewStyle, pageKey, pageSizeKey, viewStyleKey } from '../../../../../helpers/path';
import { useFlashElement } from '../../../../../hooks/use-flash-element/use-flash-element';

const defaultParams = {
  is_by_company: true,
  offset: -tzOffset,
  with_archive: true,
};

export const useCheckInCheckOut = () => {
  const { t, getLocale, currentLang } = useLang();
  const { checkins, isLoadingFetch, fetchCheckInCheckOut, fetchCheckInCheckOutExport, pagination } =
    useCheckInCheckOutStore();

  const { fetchTags } = useTagStaffStore();
  const { staff, fetchEmployees } = useEmployeesStore();
  const { fetchBranches } = useBranchesStore();

  const { getQueryParams } = useQueryParams();
  const params = getQueryParams();
  const employee = params.employee;
  const date = params.date;
  const checkin = params.checkin;
  const checkout = params.checkout;
  const delay = params.delay;
  const delayCheck = params['delay-check'];
  const overtime = params.overtime;
  const overtimeCheck = params['overtime-check'];
  const onlyOpenShiftsCheck = params['open-shifts-check'];
  const onlyClosedShiftsCheck = params['closed-shifts-check'];
  const tags = params.tags;
  const branches = params.branches;
  const sort = params[sortKey];
  const sortField = getSortField(sort);
  const sortDirection = getSortValue(sort) as SortType;
  const page = params[pageKey];
  const pageSize = params[pageSizeKey];
  const viewStyle = params[viewStyleKey] || defaultViewStyle;

  const elementId = params.overlayId ? params.overlayId.split('-')[1] : undefined;
  useFlashElement({
    isReady: !isLoadingFetch && !!checkins?.length,
    selector: `[data-table-row="${elementId}"]`,
  });

  const requestParams: FetchCheckInCheckOutRequest = useMemo(() => {
    const parsedCreatedAt = formatDateTimeRangeFromString(date);

    return {
      ...defaultParams,
      employee,
      checkin,
      checkout,
      delay,
      from: parsedCreatedAt?.from,
      to: parsedCreatedAt?.to,
      delay_check: !!delayCheck,
      overtime,
      overtime_check: !!overtimeCheck,
      only_open_shifts_check: !!onlyOpenShiftsCheck,
      only_closed_shifts_check: !!onlyClosedShiftsCheck,
      field: sortField,
      dir: sortDirection,
      with_archive: false,
      page,
      page_size: pageSize,
      tags,
      branches,
    };
  }, [
    employee,
    date,
    checkin,
    checkout,
    delay,
    delayCheck,
    overtime,
    overtimeCheck,
    onlyOpenShiftsCheck,
    onlyClosedShiftsCheck,
    sortField,
    sortDirection,
    tags,
    branches,
    page,
    pageSize,
  ]);

  useEffect(() => {
    fetchCheckInCheckOut(requestParams);
  }, [requestParams]);

  useEffect(() => {
    fetchEmployees({ is_by_company: true, page: 1, page_size: 1000 });
    fetchTags({ is_by_company: true, n: 1000, since_id: 0 });
    fetchBranches({ is_by_company: true, since_id: 0, n: 1000 });
  }, []);


  const handleEnd = () => {
    if (isLoadingFetch || !pagination || pagination.is_last) return;
    const nextPage = pagination.page + 1;
    fetchCheckInCheckOut({ ...requestParams, page: nextPage, infinite: true });
  };


  const employees: (IdValue & { tags: IdValue[] })[] =
    staff?.map((item) => ({
      id: String(item.id),
      value: `${getLocale(item.surname)} ${getLocale(item.name)}`,
      tags: item.tags,
    })) || [];

  const handleExportClick = (format: CheckInExportFormatType) => {
    fetchCheckInCheckOutExport({
      format,
      language: currentLang,
      ...requestParams,
    });
  };

  const editCheckInCheckOutModal = useModalParams('editCheckInCheckOut');
  const checkinHistoryModal = useModalParams('checkinHistory');
  const nestedCheckinsModal = useModalParams('nestedCheckins');

  return {
    t,
    handleExportClick,
    employees,
    editCheckInCheckOutModal,
    checkinHistoryModal,
    nestedCheckinsModal,
    viewStyle,
    handleEnd,
  };
};
